import {
  DefaultSupervisionMetrikTypeEnum,
  DefaultSupervisionResolvedLine,
  DefaultSupervisionSource,
} from '../models/default-supervision.model';

/** Établissement rattaché à une Metrik, avec le flag « établissement gestionnaire » (`isMain`). */
export interface SupervisionEstablishmentRef {
  _id: string;
  label: string;
  isMain?: boolean;
}

/** Provenance du superviseur affiché : résolution par défaut (établissement / organisation) ou saisie manuelle. */
export type SupervisionProvenance = DefaultSupervisionSource | 'manual';

/**
 * Forme attendue par `POST default-supervision/resolve` : uniquement `_id` et `label`
 * (le flag `isMain` sert à la sélection côté front, il n'est pas transmis au back).
 */
export function toSupervisionResolvePayload(
  establishments?: SupervisionEstablishmentRef[],
): { _id: string; label: string }[] {
  return (establishments ?? []).map(establishment => ({
    _id: establishment._id,
    label: establishment.label ?? '',
  }));
}

/**
 * Règle de restitution PRED-3236 : UN SEUL superviseur par Metrik.
 * Parmi les lignes résolues (une par établissement), on retient celle de l'établissement
 * gestionnaire (`isMain`) ; à défaut celle du premier établissement rattaché ; sans
 * établissement, la première ligne (résolution au niveau de l'organisation).
 * La ligne retenue l'est même si elle est « Non couverte », afin que la fiche et la
 * pré-saisie du formulaire affichent toujours le même résultat.
 */
export function selectSupervisionLine(
  lines: DefaultSupervisionResolvedLine[] | undefined,
  establishments?: SupervisionEstablishmentRef[],
): DefaultSupervisionResolvedLine | undefined {
  if (!lines || lines.length === 0) {
    return undefined;
  }

  const findLine = (establishmentId?: string): DefaultSupervisionResolvedLine | undefined =>
    establishmentId ? lines.find(line => line.establishment?._id === establishmentId) : undefined;

  const mainEstablishment = establishments?.find(establishment => establishment.isMain === true);
  const firstEstablishment = establishments?.[0];

  return findLine(mainEstablishment?._id) ?? findLine(firstEstablishment?._id) ?? lines[0];
}

/** Libellé de provenance affiché au survol du superviseur (fiches) et sous le champ (formulaire). */
export function supervisionProvenanceLabel(
  provenance: SupervisionProvenance | undefined,
  metrikType: DefaultSupervisionMetrikTypeEnum,
): string {
  switch (provenance) {
    case 'manual':
      return metrikType === DefaultSupervisionMetrikTypeEnum.EQUIPMENT
        ? "Supervision métier au niveau de l'équipement"
        : 'Supervision métier au niveau du bâtiment';
    case 'establishment':
      return "Supervision métier au niveau de l'établissement gestionnaire";
    case 'tenant':
      return "Supervision métier au niveau de l'organisation";
    default:
      return 'Non couvert';
  }
}
